import { FormManager, Localization, StringUtils } from "@multiweb/reactCore/bridge/impl/prod/oldAppImports";
import { CancellationToken, CancellationTokenSource, RequestContext } from "@multiweb/types/multiweb";
import { ConditionParams } from "Global/Base/DataSources/Interfaces";
import { InvalidOperationException } from "Global/Base/Exceptions";
import { EOWNoImplicitFormForClassException } from "OW.Remoting/Basic/OWExceptions";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IDataLoadingAwaitable, IInteractiveControl } from "UI/UI.BaseControls/ControlInterfaces";
import { Column } from "UI/UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory } from "UI/UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { DeferredStateful, PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { Code, Key } from "../../../../Global/Base/Utils/EventUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IBooleanDetailValueEvent } from "../../../UI.BaseControls/IEvent";
import { IListDataRowElement } from "../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { DBDataSource } from "../../../UI.Controls/DataSource/DBDataSource";
import { ListDataGrid } from "../../../UI.Controls/ListDataGrid";
import { IFormGroupOpenModalAssociationParams } from "../FormManager/IFormGroup";
import { IPopupMenu } from "../IPopupMenu";
import { PopupMenu } from "../PopupMenu";
import { FrameContext } from "../UIManagerInterfaces";
import { FrameSharedMethods } from "../XGControls/Frames/FrameSharedMethods";
import { IForm } from "../XGControls/IForm";
import { ModalWindowType } from "../XGControls/ModalWindowInterfaces";
import { ControlParams, IEditableControl, IHasFrameContext } from "./ControlInterfaces";
import { Edit } from "./Edit";

export type LOVEditMasterParams = ControlParams & {
    defaultWhisperer?: string;
};

export abstract class LOVEditMaster extends Edit implements IEditableControl, IDataLoadingAwaitable, IInteractiveControl, IHasFrameContext {
    // Jméno třídy pro LOV
    private _className: string | null = null;

    protected static _functionKeyCodes: ReadonlyArray<string> = [
        Code.Tab, Code.LeftShift, Code.RightShift, Code.LeftCtrl, Code.RightCtrl, Code.LeftAlt, Code.RightAlt,
        Code.Pause, Code.CapsLock, Code.End, Code.Home, Code.ArrowLeft, Code.ArrowRight, Code.PrintScreen,
        Code.LeftWindows, Code.RightWindows, Code.F2, Code.F4, Code.F7, Code.F9, Code.F11, Code.NumLock,
        Code.ContextMenu, Code.ScrollLock,
    ];

    protected _canSelectOnBlur: boolean = false;
    /**Event objekt se nastaví ve chvíli ztráty focusu celé komponenty. Ke skutečné obsluze této události dojde později.*/
    protected _lostFocusEvent: FocusEvent;
    protected override _interactive: boolean = true;
    protected _keyUpped: boolean = false;
    protected _lovButtonClicked: boolean = false;
    protected _showByKey: boolean = false;
    /** Oddělovač hodnot pro více atributů v LOV */
    protected _separator: string = "\u2223"; // \u2223 je matematicke svislitko, vypada temer stejne jako |, ale je to jiny znak
    /**
     * slouži jako znak, který se použije jako náhrada za _separator, pokud se v textu objeví znak _separator
     * @protected
     */
    protected _separatorAlternative: string = "|";

    protected _lovButton: HTMLElement;
    protected _ownerForm: IForm;

    protected _dropDown?: IPopupMenu;
    protected _dataSource: DBDataSource | undefined;
    protected _whisperer: ListDataGrid<IListDataRowElement> | undefined;

    protected _actualObject: OWObject;

    protected _editWindow: string;

    protected _waitOnDataLoadedDeffer: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();

    protected _mainCondition: string;

    protected _condition: string;
    protected _showDetail: boolean = true;
    protected _conditionParamsAttrs?: string[];
    protected _currentConditionParams?: ConditionParams;
    // Drží handler na timeout vytvořený při keyUp
    protected _keyUpTimeout: number;
    /** Prodleva, o kterou je zpomaleno vyhledávání v našeptávači po zadaní posledního znaku */
    protected _keyupInterval: number = 500;

    protected _objectChanging: Promise<boolean> = null;

    protected _defaultWhisperer: string;
    protected _isModalLoading: boolean = false; // True pokud se zrovna otevírá modální okno LOV

    protected _pendingTask: Promise<void> = null;
    protected _pendingTaskCTS: CancellationTokenSource;

    /**
     * Informace o tom, zda napsaná hodnota je právě zadávána uživatelem, a tedy by nemělo dojít k jejímu smazání ani při změně objektu
     */
    protected _whispererDataLoading: boolean = false;
    //#endregion Fields

    //#region Properties
    /**
     * Vyžadováno pro otevírání modálního formuláře
     */
    public frameContext: FrameContext | undefined = undefined;
    public set ownerForm(value: IForm) {
        this._ownerForm = value;
    }

    public override set interactive(value: boolean) {
        this._interactive = value;
    }

    public override get interactive(): boolean {
        return this._interactive;
    }

    public get className(): string | null {
        return this._className;
    }

    public set className(value: string | null) {
        this._className = value;
    }

    //#endregion Properties

    //#region Constructors
    protected constructor({ defaultWhisperer, ...otherParams }: LOVEditMasterParams) {
        super(otherParams);

        this.element.classList.add("LOVEdit", "loader-enabled");
        this.attrName = this.getDataAttr("AttrName");

        this._defaultWhisperer = defaultWhisperer;

        this._editWindow = this.getDataAttr("EditWindow");
        this._condition = this.getDataAttr("Condition");
        this._conditionParamsAttrs = this.getDataAttr("ConditionParams")?.split(";");

        this._showDetail = DataConverter.toBoolean(this.element.getAttribute("data-ShowDetail"), true);

        this._lovButton = this._createValuePicker();

        this.element.appendChild(this._lovButton);

        this._setCaptionReference(this.innerElement);
        this._waitOnDataLoadedDeffer.resolve();
    }
    //#endregion Constructors

    //#region Methods
    public async waitOnDataLoaded(): Promise<boolean> {
        return this._waitOnDataLoadedDeffer.promise;
    }

    /**
     * Metoda volána při změně objektu (výběrem z našeptávače nebo z modálního okna)
     * @param condition
     * @param callingEvent
     * @param btnClick - pokud je vyvoláno klikem na tlačítko LOV tak je true
     * @returns boolean promise - status průběhu, true v pořádku
     */
    protected async _changeObject(condition: string, callingEvent: Event, btnClick: boolean = false): Promise<boolean> {
        if (this._keyUpped) {
            return false;
        }

        this._currentConditionParams = this._conditionParamsAttrs ? await this._actualObject.getParameters(this._conditionParamsAttrs) : undefined;

        let finalCondition: string = condition ? condition : "ID <> 0";

        if (this._mainCondition != null)
            finalCondition = this._mainCondition + " AND (" + finalCondition + ")";

        if (!this._dropDown) {
            await this._initializeDropDown(callingEvent);
        }

        if (this._dropDown.visible && btnClick)
            this.hideDropDown();

        this._dataSource.condition = finalCondition;
        this._dataSource.conditionParams = this._currentConditionParams;
        return true;
    }

    /**
     * Inicializace DropDown
     * @param _callingEvent
     */
    protected async _initializeDropDown(_callingEvent: Event): Promise<void> {
        this._dropDown = new PopupMenu(null, true, "calc(100%-1px)", null, "0", "0");
        this._events.addListener(document, "click", event => {
            //pokud je po kliknutí nastaven objekt _lostFocusEvent indikuje to událost ztráty focusu a dojde k obsloužení této události
            if (this._lostFocusEvent) {
                //funkce canSelectOnBlur se aplikuje pouze pokud našeptávač nemá focus
                this._canSelectOnBlur = !(event.target instanceof Element && event.target.closest(".LOVWhisperer") != null);
                this._lovOnBlur(true);
                this.lostFocus.trigger(this, this._lostFocusEvent);
                this._lostFocusEvent = undefined;
            }
        });
        this.hideDropDown();

        await this._initializeWhisperer();

        this._dropDown.element.classList.add("LOVWhisperer");
        this._dropDown.element.id = "values" + this.element.id;
        document.body.appendChild(this._dropDown.element);
    }

    /**
     * Pozicování dropDown menu s našeptávačem
     */
    public async showDropDown(): Promise<void> {
        if (this._errorPopup)
            this._errorPopup.setVisibility(false);
        if (!this._whisperer) {
            if (!await this._initializeWhisperer()) {
                return;
            }
        }
        //při zviditelnění dropdown resetovat flag _canSelectOnBlur
        if (!this._dropDown?.visible) {
            this._canSelectOnBlur = false;
        }
        this._dropDown?.setVisibility(true);
        this._recalculateDropDownPosition();
    }

    /**
     * Skrytí DropDown našeptávače
     */
    public hideDropDown(): void {
        if (this._dropDown) {
            this._dropDown.setVisibility(false);

            if (this._whisperer) {
                this._whisperer.clearSelectedRows();
            }
        }
    }

    /**
     * Přepočítá pozici DropDown našeptávače
     */
    protected _recalculateDropDownPosition(): void {
        const rect = this.element.getBoundingClientRect();
        if (this._whisperer.dataRows.length === 0) {
            this._dropDown.element.style.height = "200px";
        } else {
            this._dropDown.element.style.height = "min-content";
            if (document.documentElement.classList.contains("safari")) {
                this._dropDown.element.style.height = "-webkit-fill-available";
            }
        }

        this.dropDownElementPosition(this._dropDown, this._dropDown.element, null, rect.width, this._dropDown.element.getBoundingClientRect().height || 200);
    }
    /**
     * Inicializace našeptávače (datagridu)
     */
    protected async _initializeWhisperer(): Promise<boolean> {
        // může nastat situace, kdy v této části ještě není nastavena vlastnost className (např. objekt byl inicializován později než LOV viz. https://tfs/Jadro/Jadro%20.NET/_workitems/edit/36080)
        if (this.className == null) {
            const className = await this._actualObject.getObjectAttributeClass(this.attrName); // proto se pro správné fungování pokusí získat className
            if (className != null) {
                this.className = className;
            } else {
                return false;
            }
        }

        if (this._conditionParamsAttrs && this._currentConditionParams === undefined) {
            this._currentConditionParams = await this._actualObject.getParameters(this._conditionParamsAttrs);
        }

        this._dataSource = new DBDataSource({
            className: this.className,
            condition: this._condition,
            conditionParams: this._currentConditionParams,
            frameContext: { ...this.frameContext, roles: this._actualObject.editRoleIds },
        });

        if (this._dropDown) {
            this._dropDown.element.setAttribute("data-objectname", this.className);
            this._dropDown.element.setAttribute("data-height", "300");
            this._dropDown.element.setAttribute("data-width", "300");
        }

        this._whisperer = new ListDataGrid<IListDataRowElement>();
        this._whisperer.removeAttribute("tabindex");
        this._whisperer.multiSelect = false;
        this._whisperer.renderHeader = false;
        this._whisperer.renderControlElement = false;
        this._whisperer.dataSource = this._dataSource;
        this._whisperer.objectName = this.className;
        this._whisperer.autoSelectFirstRecord = false;

        if (this._whisperer.canValueLoad) {
            this._createWhispererColumns();
        } else {
            this._whisperer.registerEventListener("canvalueload", async (e: IBooleanDetailValueEvent) => {
                if (!this._whisperer.initialized && e.detail.value) {
                    this._createWhispererColumns();
                }
            });
        }

        this._events.addListener(this._whisperer, "DataShown", () => {
            this._recalculateDropDownPosition();
        });

        if (this._dropDown) {
            this._dropDown.element.classList.add("dataGrid");
            this._dropDown.element.appendChild(this._whisperer);
        }
        return true;
    }

    protected abstract _createWhispererColumns(): void;

    /**
     * Vytvoří seznam sloupců
     * @param showAttrs - Pole atributů k zobrazení
     */
    protected async _createColumns(showAttrs: string[]): Promise<any> {
        const columns = new Array<Column>();
        let attrs = new Array<string>();

        try {
            const attrInfos = await ModelDescriptionsManager.instance.getAttributesInfo(this.className, showAttrs);
            for (const showAttr of showAttrs) {
                const attrInfo = attrInfos.find(info => StringUtils.compareCaseInsensitive(info.attributeName, showAttr));
                const col: Column = await ColumnFactory.initColumn({ attrName: attrInfo.attributeName, dataType: "attribute" }, attrInfo.type);

                if (!col.visible)
                    continue;

                columns.push(col);
                attrs = attrs.concat(col.dataSourceAttrNames);

                this._whisperer.appendChild(col);
            }

            this._dataSource.setAttrs(attrs);
            this._whisperer.columns = columns;
        } finally {
            // Prazdne finally
        }
    }

    protected _loadDataToWhisperer(event: Event): Promise<boolean> {
        this._keyUpped = false;
        let value = this.innerElement.value;
        if (event instanceof ClipboardEvent) {
            value = event.clipboardData.getData("text/plain")?.trim();
        }
        if (value === "") {
            this.hideDropDown();
        }

        if (this._dropDown) {
            this._whisperer?.clearSelectedRows();
        }

        this._objectChanging = this._changeObject(value, event);

        return this._objectChanging;
    }

    protected _checkClickEvent(event: MouseEvent): boolean {
        if (this._isModalLoading || event.ctrlKey || event.metaKey) {
            return false;
        }
        event.stopPropagation();
        return this._isModalLoading = true;
    }

    /**
     * Nastavení společných listenerů pro LOV
     */
    protected _setDefaultListeners(): void {
        this._events.addListener(this.innerElement, "keydown", async (e: KeyboardEvent) => {
            // tab
            if (this._dropDown && e.key === Key.Tab) {
                this.hideDropDown();
            }
        });

        this._events.addListener(this.innerElement, "paste", (e) => {
            if (this._waitOnDataLoadedDeffer.isResolved) {
                this._waitOnDataLoadedDeffer = PromiseUtils.deferStateful<boolean>();
            }
            e.stopPropagation();
            this._loadDataToWhisperer(e);
        });

        // Je potřeba, aby se obnovil objekt na LF po "Zrušit změny" a tím se nastavila původní hodnota LOVEditu.
        this._events.addListener(this.innerElement, "change", () => {
            this._actualObject.isChanged = true;
        });
    }

    protected async _keyboardEventHandler(e: KeyboardEvent): Promise<void> {
        if (!this.isEditing || !this.enabled) { // Není v editaci nebo je ReadOnly
            return;
        }

        if (this._dropDown?.visible) {
            switch (e.key) {
                case Key.Enter: {
                    e.stopPropagation();
                    if (this._whisperer.selectedRows.length > 0) {
                        this._whisperer.confirmSelection();
                    } else {
                        this.hideDropDown();
                    }
                    return;
                }
                case Key.Escape: {
                    this.hideDropDown();
                    return;
                }
                case Key.ArrowUp: {
                    e.preventDefault(); // Vypnutí defaultní funkcionality přemístění kurzoru na začátek inputu.
                    e.stopPropagation();
                    this._whisperer.prevRecord(false);
                    return;
                }
                case Key.ArrowDown: {
                    e.preventDefault();
                    e.stopPropagation();
                    this._whisperer.nextRecord(false);
                    return;
                }
            }
        } else {
            switch (e.key) {
                case Key.Enter: {
                    this._showByKey = true;
                    this._changeObject(this.innerElement.value, e, true);
                    return;
                }
            }
        }

        // U následujících kláves je zakázána propagace, aby se pomocí nich dalo pohybovat
        // v textu inputu, ne v záznamech DataGridu.
        const stopPropagationKeys: string[] = [Key.Home, Key.End, Key.PageUp, Key.PageDown];
        if (stopPropagationKeys.includes(e.key)) {
            e.stopPropagation();
        }

        // Reakce na funkční klávesy není žádoucí, ale kombinace Ctrl + některé klávesy je žádoucí
        // Pro CTRL+V se používá paste event, takže toto se nezachytává
        const allowedCtrlKeys = ["KeyX", "Backspace", "Delete"];
        if (LOVEditMaster._functionKeyCodes.includes(e.code) || (e.ctrlKey && !allowedCtrlKeys.includes(e.code))) {
            return;
        }

        clearTimeout(this._keyUpTimeout);
        this._keyUpped = true;
        if (this._waitOnDataLoadedDeffer.isResolved) {
            this._waitOnDataLoadedDeffer = PromiseUtils.deferStateful<boolean>();
        }
        this._keyUpTimeout = self.setTimeout(async () => {
            await this._loadDataToWhisperer(e);
        }, this._keyupInterval);
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }

    protected _lovOnBlur(hideDropDown: boolean): void {
        if (this._waitOnDataLoadedDeffer.isSettled) {
            this._waitOnDataLoadedDeffer = PromiseUtils.deferStateful<boolean>();
        }

        const { promise, cts } = PromiseUtils.cancelPrevAndDelayCall(this._pendingTaskCTS, this._keyupInterval, async () => {
            if (this._objectChanging)
                await Promise.resolve(this._objectChanging);

            if (!this._whispererDataLoading) {
                this._waitOnDataLoadedDeffer.resolve();
                this._selectCurrentOnBlur();
            }

            this.validate(); // Uvidíme jestli nebude dělat problémy při kliknutí na button otevírající modal.
        });

        promise.catch(PromiseUtils.cancellationCatch);
        this._pendingTask = promise;
        this._pendingTaskCTS = cts;
        if (hideDropDown) this.hideDropDown();
    }

    /**
     * Pokud se ztrácí focus a právě jeden záznam odpovídá porovnání (ne substring), pak je tento záznam vybrán
     */
    private _selectCurrentOnBlur(): void {
        if (!this._whisperer || !this._whisperer.dataSource || !this._canSelectOnBlur) return;

        // Zajímají nás první dva záznamy, pokud se nerovná druhý záznam, pak ani žádný další
        const [ firstRecord, secondRecord ] = [...this._whisperer.dataSource];
        if (firstRecord === undefined || secondRecord === undefined) {
            return;
        }

        const canBeSelected = { canSelectFirstRecord: true, canSelectSecondRecord: true };
        const values = super.controlValue.split(` ${this._separator} `);
        const attrs = this._whisperer.dataSource.getAttrs() ?? [];
        if (values?.length !== attrs?.length) return; // pokud je více atributů, uživatel musí zadat všechny oddělené separátorem

        // Prochází všechny atributy v našeptávači a porovnává je s vepsanou hodnotou
        let index: number = 0;
        attrs.forEach(attr => {
            canBeSelected.canSelectFirstRecord &&= StringUtils.compareCaseInsensitive(values[index], firstRecord[attr]?.toString());
            canBeSelected.canSelectSecondRecord &&= StringUtils.compareCaseInsensitive(values[index], secondRecord[attr]?.toString());
            index++;
        });

        if (canBeSelected.canSelectFirstRecord === canBeSelected.canSelectSecondRecord) return; // pokud můžu vybrat oba záznamy nebo ani jeden, nevyberu žádný

        //na základě předešlého vyhodnocení získám dataKey záznamu, který se má vybrat a vyberu ho
        const selectedDataKey = (canBeSelected.canSelectFirstRecord ? firstRecord : secondRecord)[this._whisperer.dataSource.dataKey];
        this._whisperer.selectRow(selectedDataKey.toString());
    }

    /** Otevře modální okno s detailem objektu ve vazbě. */
    public static async openDetailForm(params: IFormGroupOpenModalAssociationParams, type: ModalWindowType): Promise<void> {
        let isImplicit = false;
        let formName = params.formName;

        if (FrameSharedMethods.isFormNameImplicit(formName)) {
            const ownerFormName = params.parametricData.ownerForm?.formName;
            if (!ownerFormName)
                throw new InvalidOperationException("[LOV] Missing ownerForm to get IMPLICIT form.");
            const formInfo = await params.object.getImplicitForm(params.attrName, `${ownerFormName}${formName.substring(8)}`);
            if (!formInfo || !formInfo.FormName)
                throw new EOWNoImplicitFormForClassException("");
            formName = formInfo.FormName;
            isImplicit = true;
        }

        params = {
            ...params,
            formName: formName,
            type: type,
            parametricData: {
                ...params.parametricData,
                viewMode: false,
                autoSelectFirstRow: true,
                masterObject: null,
                masterLinkToAttr: null,
                setValues: null,
                selectedID: null,
                openInEdit: false,
                isImplicit: isImplicit,
            },
        };

        await FormManager.instance.openFormModal(params);
    }

    protected override _createValuePicker(): HTMLElement {
        const picker = super._createValuePicker();
        Localization.addLocData(picker, "GUI-798822", ["title", "ariaLabel"]); // Výběr ze seznamu
        return picker;
    }

    //#endregion Methods

    //#region Dispose
    public override dispose(): void {
        this._actualObject = null;
        this._dropDown?.element.remove();
        this._whisperer?.dispose();
        super.dispose();
    }
    //#endregion Dispose
}
